import { useNavigate } from 'react-router-dom'
import {
  Mail,
  Building2,
  MapPin,
  Clock,
  Bell,
  RefreshCw,
  MessageSquare,
  Video,
  PhoneCall,
  CalendarDays,
  Users,
  Star,
  ChevronRight,
} from 'lucide-react'
import type { ToolCallEvent } from '@/hooks/use-chat'

// ── Avatar color (same logic as ContactsPage) ─────────────

const AVATAR_COLORS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#0F2E28',
  '#FF6F61',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
]

function hashName(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

function avatarColor(firstName: string, lastName?: string | null): string {
  const name = `${firstName}${lastName ?? ''}`
  return AVATAR_COLORS[hashName(name) % AVATAR_COLORS.length]
}

function initials(firstName: string, lastName?: string | null): string {
  const first = firstName?.charAt(0) ?? ''
  const last = lastName?.charAt(0) ?? ''
  return (first + last).toUpperCase() || '?'
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never'
  const diff = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ── Types (matching API result shapes) ────────────────────

interface ContactResult {
  id: string
  firstName: string
  lastName?: string | null
  email?: string | null
  phone?: string | null
  company?: string | null
  jobTitle?: string | null
  location?: string | null
  avatarUrl?: string | null
  lastContactedAt?: string | null
  similarityScore?: number
}

interface ReminderResult {
  id: string
  title: string
  type: string
  remindAt: string
  recurring: 'none' | 'weekly' | 'monthly' | 'yearly'
  contactId?: string | null
  sent?: boolean
}

interface InteractionResult {
  id: string
  type: string
  title?: string | null
  notes?: string | null
  location?: string | null
  quality?: string | null
  occurredAt: string
  contactId: string
}

// ── Contact Card ──────────────────────────────────────────

function ContactCard({
  contact,
  compact,
  onSelect,
}: {
  contact: ContactResult
  compact?: boolean
  onSelect?: (contact: ContactResult) => void
}) {
  const navigate = useNavigate()
  const bg = avatarColor(contact.firstName, contact.lastName)

  const handleClick = () => {
    if (onSelect) {
      onSelect(contact)
    } else {
      navigate(`/contacts/${contact.id}`)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="artifact-card bg-surface-container-high/60 hover:bg-surface-container-highest/80 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors"
    >
      {contact.avatarUrl ? (
        <img
          src={contact.avatarUrl}
          alt={`${contact.firstName} ${contact.lastName ?? ''}`}
          className="size-10 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
          style={{ backgroundColor: bg }}
        >
          {initials(contact.firstName, contact.lastName)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-foreground truncate font-medium">
            {contact.firstName} {contact.lastName ?? ''}
          </span>
          {contact.similarityScore != null && contact.similarityScore < 1 && (
            <span className="bg-primary/10 text-primary shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
              {Math.round(contact.similarityScore * 100)}%
            </span>
          )}
        </div>
        {!compact && (
          <div className="text-on-surface-variant mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            {contact.jobTitle && contact.company ? (
              <span className="flex items-center gap-1 truncate">
                <Building2 className="size-3 shrink-0" />
                {contact.jobTitle} at {contact.company}
              </span>
            ) : contact.company ? (
              <span className="flex items-center gap-1 truncate">
                <Building2 className="size-3 shrink-0" />
                {contact.company}
              </span>
            ) : contact.jobTitle ? (
              <span className="truncate">{contact.jobTitle}</span>
            ) : null}
            {contact.lastContactedAt && (
              <span className="flex items-center gap-1">
                <Clock className="size-3 shrink-0" />
                {relativeTime(contact.lastContactedAt)}
              </span>
            )}
          </div>
        )}
      </div>
      <ChevronRight className="text-on-surface-variant/50 size-4 shrink-0" />
    </button>
  )
}

// ── Reminder Card ─────────────────────────────────────────

function ReminderCard({ reminder }: { reminder: ReminderResult }) {
  return (
    <div className="artifact-card bg-surface-container-high/60 flex items-start gap-3 rounded-xl px-3 py-2.5">
      <div className="bg-primary/10 mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full">
        <Bell className="text-primary size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-foreground font-medium">{reminder.title}</p>
        <div className="text-on-surface-variant mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
          <span className="flex items-center gap-1">
            <CalendarDays className="size-3 shrink-0" />
            {formatDateTime(reminder.remindAt)}
          </span>
          {reminder.recurring !== 'none' && (
            <span className="tag-lilac flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
              <RefreshCw className="size-2.5" />
              {reminder.recurring}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Interaction Card ──────────────────────────────────────

const INTERACTION_ICONS: Record<string, typeof MessageSquare> = {
  meeting: Users,
  call: PhoneCall,
  video: Video,
  email: Mail,
  message: MessageSquare,
}

const QUALITY_COLORS: Record<string, string> = {
  great: 'text-chartreuse-foreground fill-chartreuse',
  good: 'text-chartreuse-foreground fill-chartreuse/70',
  neutral: 'text-on-surface-variant',
  poor: 'text-coral fill-coral/40',
  bad: 'text-coral fill-coral',
}

function InteractionCard({ interaction }: { interaction: InteractionResult }) {
  const Icon = INTERACTION_ICONS[interaction.type] || MessageSquare
  const qualityClass = interaction.quality ? (QUALITY_COLORS[interaction.quality] ?? '') : ''

  return (
    <div className="artifact-card bg-surface-container-high/60 flex items-start gap-3 rounded-xl px-3 py-2.5">
      <div className="bg-lilac-soft text-lilac-foreground mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-foreground truncate font-medium">
            {interaction.title || interaction.type}
          </p>
          {interaction.quality && <Star className={`size-3.5 shrink-0 ${qualityClass}`} />}
        </div>
        <div className="text-on-surface-variant mt-0.5 flex items-center gap-3 text-xs">
          <span className="capitalize">{interaction.type}</span>
          <span className="flex items-center gap-1">
            <Clock className="size-3 shrink-0" />
            {formatDate(interaction.occurredAt)}
          </span>
          {interaction.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="size-3 shrink-0" />
              {interaction.location}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Disambiguation Selector ───────────────────────────────

function DisambiguationSelector({
  contacts,
  onSelect,
}: {
  contacts: ContactResult[]
  onSelect: (contact: ContactResult) => void
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-on-surface-variant px-1 text-xs font-medium">
        Multiple matches found — tap to select:
      </p>
      {contacts.map((c) => (
        <ContactCard key={c.id} contact={c} compact onSelect={onSelect} />
      ))}
    </div>
  )
}

// ── Main Artifact Renderer ────────────────────────────────

interface ChatArtifactProps {
  tool: ToolCallEvent
  onSendMessage?: (text: string) => void
}

export function ChatArtifact({ tool, onSendMessage }: ChatArtifactProps) {
  if (tool.result === undefined) return null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = tool.result as Record<string, any>
  if (!result || result.error) return null

  switch (tool.name) {
    case 'search_contacts': {
      const contacts: ContactResult[] = result.contacts ?? []
      if (contacts.length === 0) return null

      // Disambiguation: multiple fuzzy results
      if (
        contacts.length > 1 &&
        contacts.length <= 4 &&
        result.matchType === 'fuzzy' &&
        onSendMessage
      ) {
        return (
          <DisambiguationSelector
            contacts={contacts}
            onSelect={(c) => onSendMessage(`I meant ${c.firstName} ${c.lastName ?? ''}`.trim())}
          />
        )
      }

      return (
        <div className="space-y-1.5">
          {contacts.map((c) => (
            <ContactCard key={c.id} contact={c} />
          ))}
        </div>
      )
    }

    case 'get_contact_details': {
      const contact: ContactResult | undefined = result.contact
      if (!contact) return null
      return <ContactCard contact={contact} />
    }

    case 'create_reminder': {
      const reminder: ReminderResult | undefined = result.reminder
      if (!reminder) return null
      return <ReminderCard reminder={reminder} />
    }

    case 'list_upcoming_reminders': {
      const reminders: ReminderResult[] = result.reminders ?? []
      if (reminders.length === 0) return null
      return (
        <div className="space-y-1.5">
          {reminders.map((r) => (
            <ReminderCard key={r.id} reminder={r} />
          ))}
        </div>
      )
    }

    case 'create_interaction': {
      const interaction: InteractionResult | undefined = result.interaction
      if (!interaction) return null
      return <InteractionCard interaction={interaction} />
    }

    case 'import_contact_from_linkedin':
    case 'create_contact': {
      const contact: ContactResult | undefined = result.contact
      if (!contact) return null
      return <ContactCard contact={contact} />
    }

    default:
      return null
  }
}
